import { AxiosInstance, AxiosResponse } from 'axios';
import { debounce } from 'lodash-es';
import { i18n } from '@/common/locale';
import {
  apiController,
  getErrorState,
  getFrameName,
  hasRequestTimeRange,
} from '@/worker/commands/config/interceptors.utils';
import { RSA_ID_HEADER, useApiRsa } from '@/common/utils/rsa/apiRsa';
import { getV7HealthCheck } from '@/api-client/common/queries/mode-controller/mode-controller';

const LOCKED_MESSAGE_CODE = 'USER_ID_IS_LOCKED';

const getHealthCheck = async (callback: (flag: boolean) => void) => {
  try {
    const {
      data: { data },
    } = await getV7HealthCheck({});
    callback(data?.at(0) === 'success');
  } catch {
    callback(false);
  }
};
const debouncedHealthCheck = debounce(getHealthCheck, 1000);

const exceptionHandler = {
  401: async () => {
    console.warn(i18n.global.t('NOTI.API.NOT_PERMISSION_LOGIN_PAGE'));
    postMessage({ action: 'logout' });
  },
  403: async () => {
    console.warn(i18n.global.t('NOTI.API.NOT_PERMISSION'));
    postMessage({ action: 'updateToken' });
  },
  400: () => {
    console.warn(i18n.global.t('NOTI.API.INVALID_REQUEST'));
  },
  405: () => {
    console.warn(i18n.global.t('NOTI.API.NOT_ALLOW_METHOD'));
  },
  406: (response: AxiosResponse) => {
    const isLockedUser = response?.data?.message === LOCKED_MESSAGE_CODE;
    const message = isLockedUser
      ? i18n.global.t('MESSAGE.LOCKED_ACCOUNT')
      : i18n.global.t('MESSAGE.NO_PERMISSION');

    if (isLockedUser && getErrorState('LOCKED_USER').isLogout) {
      return;
    }

    postMessage({
      action: 'showMessage',
      type: 'error',
      message,
      useHTML: isLockedUser,
    });

    if (isLockedUser) {
      getErrorState('LOCKED_USER').isLogout = true;
      postMessage({ action: 'logout' });
    }
  },
  408: (response: AxiosResponse) => {
    const key = hasRequestTimeRange(response?.config)
      ? 'NOTI.API.TIMEOUT_EXCEEDED'
      : 'NOTI.API.TIMEOUT_EXCEEDED_RETRY';
    console.warn(i18n.global.t(key));
    postMessage({
      action: 'showMessage',
      type: 'error',
      message: i18n.global.t(key),
    });
  },
  500: () => {
    console.warn(i18n.global.t('NOTI.API.SERVER_ERROR'));
  },
  502: () => {
    // TODO 추후 메시지 수정
    console.warn(i18n.global.t('NOTI.API.SERVER_ERROR'));
    postMessage({ action: 'systemError' });
  },
  503: () => {
    console.warn(i18n.global.t('NOTI.API.NOT_AVAILABLE_SERVICE'));
  },
  504: () => {
    //* 504 에러 발생시 Health Check 호출 > 상태 success일때 에러 페이지 호출 안함. 다중 호출 방지로 debounce 1초
    debouncedHealthCheck((status) => {
      if (!status) {
        console.warn(i18n.global.t('NOTI.API.SERVER_ERROR'));
        postMessage({ action: 'systemError' });
      }
    });
  },
};

export const setInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (req) => {
      if (req?.params?.frameName) {
        req.headers.frameName = encodeURIComponent(req.params.frameName);
        delete req.params.frameName;
      } else if (req?.data?.frameName) {
        req.headers.frameName = encodeURIComponent(req.data.frameName);
        delete req.data.frameName;
      } else if ((req as any)?.frameName) {
        req.headers.frameName = encodeURIComponent((req as any).frameName);
        delete (req as any).frameName;
      }

      // skipGlobalErrorNoti: payload 플래그를 헤더로 옮기고 payload 에서 제거(서버 미전송).
      // 응답 인터셉터가 이 헤더를 보면 전역 에러 노티(exceptionHandler)를 건너뛴다.
      if (req?.params?.skipGlobalErrorNoti || req?.data?.skipGlobalErrorNoti) {
        req.headers.skipGlobalErrorNoti = true;
        if (req.params?.skipGlobalErrorNoti) {
          delete req.params.skipGlobalErrorNoti;
        }
        if (req.data?.skipGlobalErrorNoti) {
          delete req.data.skipGlobalErrorNoti;
        }
      }

      if (req && (req?.params?.isPolling || req?.data?.isPolling)) {
        req.headers.isPolling = true;

        if (req.params?.isPolling) {
          delete req.params.isPolling;
        } else if (req.data?.isPolling) {
          delete req?.data?.isPolling;
        }

        const { frameName } = req.headers;
        const isValid = apiController.isAvailableRequest(req, frameName);
        if (isValid) {
          apiController.request(req, frameName);
          return req;
        }

        return Promise.reject({ ignoreRequest: true });
      }

      if (req?.data?.[RSA_ID_HEADER]) {
        const key = req.data[RSA_ID_HEADER];
        req.headers[RSA_ID_HEADER] = useApiRsa().getRsaId({ key });
        delete req.data[RSA_ID_HEADER];
      }

      return req;
    },
    (e) => Promise.reject(e),
  );
  instance.interceptors.response.use(
    (res) => {
      const { frameName, isPolling } = res.config?.headers ?? {};
      if (frameName) {
        postMessage({
          action: 'setApiTraceInfo',
          frameName: decodeURIComponent(getFrameName(frameName)),
          apiTraceInfo: JSON.stringify(res),
          isError: false,
        });
      }

      if (res?.config && isPolling) {
        apiController.response(res.config, frameName);
      }

      getErrorState('LOCKED_USER').isLogout = false;

      return res;
    },
    (e) => {
      if (e?.code === 'ERR_CANCELED' || e?.ignoreRequest) {
        const { frameName: cancelFrameName, isPolling: cancelIsPolling } = e.config?.headers ?? {};
        if (e?.code === 'ERR_CANCELED' && e?.config && cancelIsPolling) {
          apiController.response(e.config, cancelFrameName);
        }
        return Promise.reject(e);
      }

      const { frameName, isPolling } = e.config?.headers ?? {};
      if (frameName) {
        postMessage({
          action: 'setApiTraceInfo',
          frameName: decodeURIComponent(getFrameName(frameName)),
          apiTraceInfo: JSON.stringify(e.response),
          isError: true,
        });
      }

      if (e?.config && isPolling) {
        apiController.response(e.config, frameName);
      }

      if (e?.response?.status && !e.config?.headers?.skipGlobalErrorNoti) {
        exceptionHandler?.[e.response.status]?.(e.response);
      } else if (e?.request) {
        console.log(i18n.global.t('NOTI.API.NOT_RECEIVE')); // 취소 시 로그 => 빌드시엔 안보이게
      } else if (!e?.ignoreRequest && !e.config?.headers?.isPolling) {
        console.warn(i18n.global.t('NOTI.API.REQUEST_ERROR'));
      }
      return Promise.reject(e);
    },
  );

  return instance;
};
